/**
 * Copyright (c) 2017-2023 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author David Sehnal <david.sehnal@gmail.com>
 */
import { Mat4, Tensor, Vec3 } from '../../../mol-math/linear-algebra';
import { SymmetryOperator } from '../../../mol-math/geometry/symmetry-operator';
import { Assembly } from '../../../mol-model/structure/model/properties/symmetry';
import { Queries as Q } from '../../../mol-model/structure';
import { StructureProperties } from '../../../mol-model/structure';
export function createAssemblies(pdbx_struct_assembly, pdbx_struct_assembly_gen, pdbx_struct_oper_list) {
    if (!pdbx_struct_assembly._rowCount)
        return [];
    var matrices = getMatrices(pdbx_struct_oper_list);
    var assemblies = [];
    for (var i = 0; i < pdbx_struct_assembly._rowCount; i++) {
        assemblies[assemblies.length] = createAssembly(pdbx_struct_assembly, pdbx_struct_assembly_gen, i, matrices);
    }
    return assemblies;
}
function createAssembly(pdbx_struct_assembly, pdbx_struct_assembly_gen, index, matrices) {
    var id = pdbx_struct_assembly.id.value(index);
    var details = pdbx_struct_assembly.details.value(index);
    var generators = [];
    var assembly_id = pdbx_struct_assembly_gen.assembly_id, oper_expression = pdbx_struct_assembly_gen.oper_expression, asym_id_list = pdbx_struct_assembly_gen.asym_id_list;
    for (var i = 0, _i = pdbx_struct_assembly_gen._rowCount; i < _i; i++) {
        if (assembly_id.value(i) !== id)
            continue;
        generators[generators.length] = {
            assemblyId: id,
            expression: oper_expression.value(i),
            asymIds: asym_id_list.value(i)
        };
    }
    return Assembly.create(id, details, operatorGroupsProvider(generators, matrices));
}
export function operatorGroupsProvider(generators, matrices) {
    return function () {
        var groups = [];
        var operatorOffset = 0;
        for (var i = 0; i < generators.length; i++) {
            var gen = generators[i];
            var operatorList = parseOperatorList(gen.expression);
            var operatorNames = expandOperators(operatorList);
            var operators = getAssemblyOperators(matrices, operatorNames, operatorOffset, gen.assemblyId);
            var selector = Q.generators.atoms({ chainTest: Q.pred.and(Q.pred.eq(function (ctx) { return StructureProperties.unit.operator_name(ctx.element); }, SymmetryOperator.DefaultName), Q.pred.inSet(function (ctx) { return StructureProperties.chain.label_asym_id(ctx.element); }, gen.asymIds)) });
            groups[groups.length] = { selector: selector, operators: operators, asymIds: gen.asymIds };
            operatorOffset += operators.length;
        }
        return groups;
    };
}
export function getMatrices(pdbx_struct_oper_list) {
    var id = pdbx_struct_oper_list.id, matrix = pdbx_struct_oper_list.matrix, vector = pdbx_struct_oper_list.vector, _schema = pdbx_struct_oper_list._schema;
    var matrices = new Map();
    var t = Vec3();
    for (var i = 0, _i = pdbx_struct_oper_list._rowCount; i < _i; i++) {
        var m = Tensor.toMat4(Mat4(), _schema.matrix.space, matrix.value(i));
        Tensor.toVec3(t, _schema.vector.space, vector.value(i));
        Mat4.setTranslation(m, t);
        Mat4.setValue(m, 3, 3, 1);
        matrices.set(id.value(i), m);
    }
    return matrices;
}
function expandOperators(operatorList) {
    var ops = [];
    var currentOp = [];
    for (var i = 0; i < operatorList.length; i++)
        currentOp[i] = '';
    expandOperators1(operatorList, ops, operatorList.length - 1, currentOp);
    return ops;
}
function expandOperators1(operatorNames, list, i, current) {
    if (i < 0) {
        list[list.length] = current.slice(0);
        return;
    }
    var ops = operatorNames[i], len = ops.length;
    for (var j = 0; j < len; j++) {
        current[i] = ops[j];
        expandOperators1(operatorNames, list, i - 1, current);
    }
}
function getAssemblyOperators(matrices, operatorNames, startIndex, assemblyId) {
    var operators = [];
    var index = startIndex;
    for (var _a = 0, operatorNames_1 = operatorNames; _a < operatorNames_1.length; _a++) {
        var op = operatorNames_1[_a];
        var m = Mat4.identity();
        for (var i = 0; i < op.length; i++) {
            Mat4.mul(m, m, matrices.get(op[i]));
        }
        index++;
        operators[operators.length] = SymmetryOperator.create("ASM_".concat(index), m, { assembly: { id: assemblyId, operId: index, operList: op } });
    }
    return operators;
}
function parseOperatorList(value) {
    // '(X0)(1-5)' becomes [['X0'], ['1', '2', '3', '4', '5']]
    // kudos to Glen van Ginkel.
    var oeRegex = /\(?([^\(\)]+)\)?]*/g, groups = [], ret = [];
    var g;
    while (g = oeRegex.exec(value))
        groups[groups.length] = g[1];
    groups.forEach(function (g) {
        var group = [];
        g.split(',').forEach(function (e) {
            var dashIndex = e.indexOf('-');
            if (dashIndex > 0) {
                var from = parseInt(e.substring(0, dashIndex)), to = parseInt(e.substr(dashIndex + 1));
                for (var i = from; i <= to; i++)
                    group[group.length] = i.toString();
            }
            else {
                group[group.length] = e.trim();
            }
        });
        ret[ret.length] = group;
    });
    return ret;
}
